import { Plus, Sparkles } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router";

import { useHasOrgRole } from "@/providers/auth/hooks";
import { EmptyState } from "@/shared/components/EmptyState";
import {
  AutoIngestSwitch,
  IngestedBy,
} from "@/shared/components/ingestionRules";
import { PatternChips } from "@/shared/components/PatternChips";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import {
  qualifyPrincipal,
  ResourceView,
  useResourceView,
} from "@/shared/components/ResourceView";
import {
  FormattedDateTime,
  formatRelativeTime,
  isRecent,
} from "@/shared/datetime";
import {
  ONLY_ADMINS_CHANGE_RULES,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";
import {
  useCreateDefaultIngestionRules,
  useDefaultIngestionRules,
  useIngestionRules,
} from "@/shared/domain/ingestionRules/hooks";
import { Banner } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import {
  cellStyles,
  DataTable,
  deriveLoadingState,
  FLEX_FILL_COL_DEF,
  useInMemoryRows,
  useRelativeTick,
  type ColDef,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./IngestionRulesList.module.css";

const RULE_SORT_KEYS = {
  name: (rule: IngestionRuleRecord) => rule.name,
  modified: (rule: IngestionRuleRecord) => new Date(rule.modified),
};

/**
 * Every ingestion rule in the org: the Settings → Ingestion tab. Members read
 * it; only org admins change rules.
 */
export function IngestionRulesList() {
  const navigate = useNavigate();
  const isAdmin = useHasOrgRole("admin");
  const rulesQuery = useIngestionRules();
  const createDefaults = useCreateDefaultIngestionRules();
  const defaultsQuery = useDefaultIngestionRules();
  // Only the defaults for new orgs: the rest are offered on the new-rule page.
  const defaultRules = (defaultsQuery.data ?? []).filter(
    (rule) => rule.for_new_orgs,
  );
  // Offered until the org has every default; while the defaults are loading,
  // or if they fail to, the button stays usable.
  const hasEveryDefault =
    defaultsQuery.isSuccess &&
    defaultRules.every((rule) => rule.already_present);
  const [error, setError] = React.useState<unknown>(null);
  // Defaults the last "Add default rules" asked for but the server didn't
  // create: it skips one whose action this organization can't run.
  const [notAdded, setNotAdded] = React.useState<readonly string[]>([]);

  const view = useResourceView<IngestionRuleRecord>({
    getRowId: (row) => row.rule_id,
    initialSort: { columnId: "name", direction: "asc" },
  });

  const allRules = React.useMemo(
    () => rulesQuery.data ?? [],
    [rulesQuery.data],
  );

  const {
    visibleRows: visibleRules,
    totalRowCount,
    hasNextPage,
  } = useInMemoryRows(view.table, allRules, RULE_SORT_KEYS);

  const addDefaults = () => {
    setError(null);
    setNotAdded([]);
    const missing = defaultRules
      .filter((rule) => !rule.already_present)
      .map((rule) => rule.name);
    createDefaults.mutate(undefined, {
      onError: setError,
      onSuccess: (created) => {
        const createdNames = new Set(created.map((rule) => rule.name));
        setNotAdded(missing.filter((name) => !createdNames.has(name)));
      },
    });
  };

  const columns = React.useMemo<ColDef<IngestionRuleRecord>[]>(() => {
    const baseColumns: ColDef<IngestionRuleRecord>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        minWidth: 200,
        sortable: true,
        valueGetter: (p) => p.data?.name ?? "",
        cellRenderer: (p: ICellRendererParams<IngestionRuleRecord>) =>
          p.data ? (
            <Link
              to={Goto.createIngestionRuleRoute(p.data.rule_id)}
              className={cellStyles.primaryContent}
            >
              {p.data.name}
            </Link>
          ) : null,
      },
      {
        colId: "files",
        headerName: "Files",
        flex: 2,
        minWidth: 220,
        sortable: false,
        // Patterns wrap onto further lines, so the row grows to fit them.
        autoHeight: true,
        wrapText: true,
        valueGetter: (p) => p.data?.include_patterns.join(", ") ?? "",
        cellRenderer: (p: ICellRendererParams<IngestionRuleRecord>) =>
          p.data ? (
            <div className={styles.cell}>
              <RulePatterns rule={p.data} />
            </div>
          ) : null,
      },
      {
        colId: "ingested_by",
        headerName: "Ingested by",
        flex: 2,
        minWidth: 240,
        sortable: false,
        autoHeight: true,
        wrapText: true,
        valueGetter: (p) => p.data?.invocation?.action.name ?? "",
        cellRenderer: (p: ICellRendererParams<IngestionRuleRecord>) =>
          p.data ? (
            <div className={styles.cell}>
              <IngestedBy rule={p.data} layout="row" />
            </div>
          ) : null,
      },
      {
        colId: "auto_ingest",
        headerName: "Auto",
        flex: 0,
        width: 90,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<IngestionRuleRecord>) =>
          p.data ? <AutoIngestSwitch rule={p.data} onError={setError} /> : null,
      },
      {
        colId: "modified",
        headerName: "Modified",
        minWidth: 160,
        sortable: true,
        autoHeight: true,
        valueGetter: (p) => p.data?.modified ?? "",
        cellRenderer: (p: ICellRendererParams<IngestionRuleRecord>) =>
          p.data ? <ModifiedCell rule={p.data} /> : null,
      },
    ];
    return baseColumns.map((column) => ({ hide: false, ...column }));
  }, []);

  const loading = deriveLoadingState({
    isLoading: rulesQuery.isLoading,
    isFetching: rulesQuery.isFetching,
  });

  const isEmpty = rulesQuery.isSuccess && allRules.length === 0;

  return (
    <div className={styles.page}>
      <p className={styles.intro} data-testid="ingestion-rules-intro">
        Rules decide which uploaded files a session waits for before it&rsquo;s
        complete, and can ingest those files automatically with an action.
      </p>
      {!isAdmin && (
        <p className={styles.intro} data-testid="ingestion-rules-admin-note">
          {ONLY_ADMINS_CHANGE_RULES}
        </p>
      )}
      {(error !== null || rulesQuery.isError) && (
        <Banner variant="error">
          {userFacingErrorMessage(error ?? rulesQuery.error)}
        </Banner>
      )}
      {notAdded.length > 0 && (
        <Banner variant="warning" data-testid="default-rules-not-added">
          {notAddedMessage(notAdded)}
        </Banner>
      )}
      {isEmpty ? (
        <EmptyState
          title="No ingestion rules yet"
          data-testid="ingestion-rules-empty"
          actions={
            isAdmin && (
              <>
                <RobotoButton
                  variant="primary"
                  icon={<Sparkles aria-hidden="true" />}
                  onClick={addDefaults}
                  disabled={createDefaults.isPending || hasEveryDefault}
                >
                  Add default rules
                </RobotoButton>
                <RobotoButton
                  variant="secondary"
                  icon={<Plus aria-hidden="true" />}
                  onClick={() =>
                    void navigate(Goto.createNewIngestionRuleRoute())
                  }
                >
                  New rule
                </RobotoButton>
              </>
            )
          }
        >
          <p>
            Without rules, uploaded files are stored as-is: no rule ingests
            them, and sessions don&rsquo;t wait for them.{" "}
            {isAdmin
              ? "Start with Roboto’s defaults, or write your own."
              : "An org admin can add Roboto’s defaults, listed below, or write rules."}
          </p>
          <p>
            Want to run analysis, reports or notifications instead? Use{" "}
            <Link to={Goto.route.Triggers}>Triggers</Link>.
          </p>
          {defaultRules.length > 0 && (
            <ul
              className={styles.defaultsList}
              data-testid="default-ingestion-rules"
            >
              {defaultRules.map((rule) => (
                <li key={rule.name} className={styles.defaultRule}>
                  <span className={styles.defaultName}>{rule.name}</span>
                  <RulePatterns rule={rule} />
                </li>
              ))}
            </ul>
          )}
        </EmptyState>
      ) : (
        <ResourceView.Root>
          <ResourceView.Actions>
            {isAdmin && (
              <div className={styles.actionsRight}>
                <RobotoButton
                  variant="primary"
                  icon={<Plus aria-hidden="true" />}
                  onClick={() =>
                    void navigate(Goto.createNewIngestionRuleRoute())
                  }
                >
                  New rule
                </RobotoButton>
              </div>
            )}
          </ResourceView.Actions>
          <DataTable<IngestionRuleRecord>
            table={view.table}
            hasNextPage={hasNextPage}
            loading={loading}
            columns={columns}
            defaultColDef={FLEX_FILL_COL_DEF}
            data={visibleRules}
            totalRowCount={totalRowCount}
            noRowsContent={<span>No ingestion rules yet.</span>}
          />
        </ResourceView.Root>
      )}
    </div>
  );
}

/** The rule's patterns, then "except" and the patterns it leaves out. */
function RulePatterns({
  rule,
}: {
  rule: Pick<IngestionRuleRecord, "include_patterns" | "exclude_patterns">;
}) {
  return (
    <span className={styles.patterns}>
      <PatternChips patterns={rule.include_patterns} />
      {rule.exclude_patterns.length > 0 && (
        <>
          <span className={styles.except}>except</span>
          <PatternChips patterns={rule.exclude_patterns} variant="exclude" />
        </>
      )}
    </span>
  );
}

/** When the rule last changed, relative while recent, and who changed it. */
function ModifiedCell({ rule }: { rule: IngestionRuleRecord }) {
  const recent = isRecent(rule.modified);
  useRelativeTick(recent);
  return (
    <div className={styles.cell}>
      {recent ? (
        <span>{formatRelativeTime(rule.modified)}</span>
      ) : (
        <FormattedDateTime value={rule.modified} time="none" />
      )}
      <RobotoPrincipalUnderlineLink
        principal={qualifyPrincipal(rule.modified_by)}
        className={styles.secondary}
      />
    </div>
  );
}

/** Which default rules weren't added, and why that happens. */
function notAddedMessage(names: readonly string[]): string {
  const list = names.join(", ");
  return names.length === 1
    ? `Couldn’t add ${list}: its action isn’t available to this organization.`
    : `Couldn’t add ${list}: their actions aren’t available to this organization.`;
}
